import { useEffect, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAccessStore, gateState, type GateState } from '@/store/access'
import type { ContentType } from '@/services/api'
import Icon from '@/components/ui/Icon'

const SAGE = '#7BA17B'
const CORAL = '#E07A5F'
const CHARCOAL = '#2D3436'
const CREAM = '#F5F2EB'

/** Fetch the access snapshot once on mount (idempotent-ish). */
export function useEnsureAccess() {
  const access = useAccessStore((s) => s.access)
  const loading = useAccessStore((s) => s.loading)
  const fetchAccess = useAccessStore((s) => s.fetchAccess)
  useEffect(() => {
    if (access) return
    // Retry a few times: the demo-mode mock interceptor attaches async, and
    // a real network blip shouldn't leave the app permanently "loading".
    let tries = 0
    let timer: ReturnType<typeof setTimeout>
    const attempt = () => {
      if (useAccessStore.getState().access) return
      fetchAccess().finally(() => {
        if (!useAccessStore.getState().access && tries++ < 5) {
          timer = setTimeout(attempt, 600)
        }
      })
    }
    attempt()
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return { access, loading }
}

/**
 * App-level hard paywall. When the trial has expired and the user has not
 * subscribed, every screen is replaced by the paywall — except the
 * subscription and settings screens (so they can pay or sign out).
 */
export function TrialLockGate({ children }: { children: ReactNode }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { access } = useEnsureAccess()

  const exempt =
    location.pathname.startsWith('/subscription') ||
    location.pathname.startsWith('/settings')

  if (access?.access_level === 'locked' && !exempt) {
    return (
      <div
        style={{
          minHeight: '100vh',
          background: CREAM,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          textAlign: 'center',
        }}
      >
        <div style={{ marginBottom: 16 }}><Icon name="lock" size={56} /></div>
        <h1 style={{ color: CHARCOAL, fontSize: 24, margin: '0 0 8px', fontWeight: 800 }}>
          Your free trial has ended
        </h1>
        <p style={{ color: CHARCOAL, opacity: 0.7, margin: '0 0 28px', maxWidth: 320 }}>
          Subscribe to unlock all 100+ activities, every printable, and all
          guided programs.
        </p>
        <button
          onClick={() => navigate('/subscription')}
          style={{
            background: SAGE,
            color: '#fff',
            border: 'none',
            borderRadius: 16,
            padding: '16px 32px',
            fontSize: 17,
            fontWeight: 700,
            cursor: 'pointer',
            width: '100%',
            maxWidth: 320,
          }}
        >
          See plans
        </button>
        <button
          onClick={() => navigate('/settings')}
          style={{
            background: 'transparent',
            color: CHARCOAL,
            opacity: 0.6,
            border: 'none',
            marginTop: 16,
            fontSize: 15,
            cursor: 'pointer',
          }}
        >
          Account settings
        </button>
      </div>
    )
  }

  return <>{children}</>
}

/** Small lock chip for list/grid cards. */
export function LockBadge({ state }: { state: GateState }) {
  if (state === 'premium' || state === 'unlocked' || state === 'loading') return null
  const label = state === 'unlockable' ? 'Tap to unlock' : 'Premium'
  return (
    <span
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        background: state === 'unlockable' ? SAGE : CORAL,
        color: '#fff',
        fontSize: 11,
        fontWeight: 700,
        padding: '4px 8px',
        borderRadius: 999,
        display: 'flex',
        alignItems: 'center',
        gap: 4,
      }}
    >
      <Icon name="lock" size={12} /> {label}
    </span>
  )
}

/**
 * Full-screen-ish locked view for a detail page. Handles the trial unlock
 * (spend a credit) and the subscribe path.
 */
export function LockedContentView({
  type,
  id,
  title,
  onUnlocked,
}: {
  type: ContentType
  id: string
  title: string
  onUnlocked: () => void
}) {
  const navigate = useNavigate()
  const access = useAccessStore((s) => s.access)
  const unlock = useAccessStore((s) => s.unlock)
  const state = gateState(access, type, id)
  const remaining = access?.credits[type]?.remaining ?? 0
  const noun = type === 'printable' ? 'printable' : 'activity'

  const handleUnlock = async () => {
    const res = await unlock(type, id)
    if (res.ok) onUnlocked()
    else navigate('/subscription')
  }

  return (
    <div
      style={{
        minHeight: '70vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        textAlign: 'center',
      }}
    >
      <div style={{ marginBottom: 12 }}><Icon name="lock" size={48} /></div>
      <h2 style={{ color: CHARCOAL, fontSize: 20, fontWeight: 800, margin: '0 0 6px' }}>
        {title}
      </h2>

      {state === 'unlockable' && (
        <>
          <p style={{ color: CHARCOAL, opacity: 0.7, margin: '0 0 24px', maxWidth: 300 }}>
            Use 1 of your <strong>{remaining}</strong> free {noun} unlocks to open
            this during your trial.
          </p>
          <button
            onClick={handleUnlock}
            style={{
              background: SAGE,
              color: '#fff',
              border: 'none',
              borderRadius: 16,
              padding: '15px 28px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
              maxWidth: 300,
            }}
          >
            Unlock ({remaining} left)
          </button>
        </>
      )}

      {(state === 'no-credits' || state === 'expired') && (
        <>
          <p style={{ color: CHARCOAL, opacity: 0.7, margin: '0 0 24px', maxWidth: 300 }}>
            {state === 'expired'
              ? 'Your free trial has ended.'
              : `You've used all your free ${noun} unlocks.`}{' '}
            Subscribe for unlimited access.
          </p>
          <button
            onClick={() => navigate('/subscription')}
            style={{
              background: SAGE,
              color: '#fff',
              border: 'none',
              borderRadius: 16,
              padding: '15px 28px',
              fontSize: 16,
              fontWeight: 700,
              cursor: 'pointer',
              width: '100%',
              maxWidth: 300,
            }}
          >
            See plans
          </button>
        </>
      )}

      <button
        onClick={() => navigate(-1)}
        style={{
          background: 'transparent',
          color: CHARCOAL,
          opacity: 0.55,
          border: 'none',
          marginTop: 16,
          fontSize: 15,
          cursor: 'pointer',
        }}
      >
        ← Go back
      </button>
    </div>
  )
}
